// components/EditProjectTaskModal.jsx
import React, { useState, useEffect } from "react";
import { X, Loader, CheckSquare, Search } from "lucide-react";
import apiService from "../services/api";
import { useTheme } from "../contexts/ThemeContext";

const EditProjectTaskModal = ({ task, onClose, onSuccess }) => {
  const { isDarkMode } = useTheme();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    projectTaskName: "",
    subject: "",
    description: "",
    status: "Not Started",
    priority: "Medium",
    assignedTo: "",
    dueDate: "",
    startDate: "",
    percentComplete: 0,
    estimatedHours: "",
    actualHours: "",
    isBlocked: false,
    blockedReason: "",
  });

  // Search states
  const [users, setUsers] = useState([]);
  const [userSearch, setUserSearch] = useState("");
  const [showUserSearch, setShowUserSearch] = useState(false);
  const [loadingUsers, setLoadingUsers] = useState(false);

  useEffect(() => {
    if (task) {
      setFormData({
        projectTaskName: task.projectTaskName || "",
        subject: task.subject || "",
        description: task.description || "",
        status: task.status || "Not Started",
        priority: task.priority || "Medium",
        assignedTo: task.assignedTo || "",
        dueDate: task.dueDate ? task.dueDate.split("T")[0] : "",
        startDate: task.startDate ? task.startDate.split("T")[0] : "",
        percentComplete: task.percentComplete || 0,
        estimatedHours: task.estimatedHours || "",
        actualHours: task.actualHours || "",
        isBlocked: task.isBlocked || false,
        blockedReason: task.blockedReason || "",
      });

      if (task.assignedUser) {
        setUserSearch(task.assignedUser.name);
      }
    }
  }, [task]);

  const searchUsers = async (query) => {
    if (!query || query.length < 2) {
      setUsers([]);
      return;
    }

    try {
      setLoadingUsers(true);
      const response = await apiService.getUsers({ search: query, limit: 10 });
      setUsers(response.data || []);
    } catch (error) {
      console.error("Error searching users:", error);
    } finally {
      setLoadingUsers(false);
    }
  };

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (userSearch) {
        searchUsers(userSearch);
      }
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [userSearch]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await apiService.updateProjectTask(task.id, formData);
      onSuccess();
    } catch (error) {
      console.error("Error updating task:", error);
      alert(error.response?.data?.message || "Failed to update task");
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const selectUser = (user) => {
    setFormData((prev) => ({
      ...prev,
      assignedTo: user.id,
    }));
    setUserSearch(user.name);
    setShowUserSearch(false);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
      <div
        className={`rounded-lg max-w-3xl w-full max-h-[90vh] overflow-hidden flex flex-col ${
          isDarkMode
            ? "bg-[#13161B] border border-gray-700"
            : "bg-white border border-gray-300"
        }`}
      >
        {/* Header */}
        <div
          className={`px-6 py-4 border-b flex items-center justify-between ${
            isDarkMode ? "border-gray-700" : "border-gray-200"
          }`}
        >
          <div className="flex items-center gap-3">
            <div
              className={`w-10 h-10 rounded-full flex items-center justify-center ${
                isDarkMode
                  ? "bg-blue-600"
                  : "bg-blue-100 border border-blue-200"
              }`}
            >
              <CheckSquare
                size={20}
                className={isDarkMode ? "text-white" : "text-blue-600"}
              />
            </div>
            <div>
              <h2
                className={`text-lg font-semibold ${
                  isDarkMode ? "text-white" : "text-gray-900"
                }`}
              >
                Edit Task
              </h2>
              <p
                className={`text-sm ${
                  isDarkMode ? "text-gray-400" : "text-gray-500"
                }`}
              >
                {task.projectTaskName}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className={`transition-colors ${
              isDarkMode
                ? "text-gray-400 hover:text-white"
                : "text-gray-500 hover:text-gray-700"
            }`}
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Form */}
        <div className="flex-1 overflow-y-auto p-6">
          <form
            onSubmit={handleSubmit}
            id="projectTaskForm"
            className="space-y-6"
          >
            {/* Basic Information */}
            <div
              className={`px-4 py-2 mb-4 rounded ${
                isDarkMode ? "bg-gray-800/50" : "bg-gray-100"
              }`}
            >
              <h3
                className={`text-sm font-semibold ${
                  isDarkMode ? "text-gray-300" : "text-gray-700"
                }`}
              >
                Basic Information
              </h3>
            </div>

            <div className="space-y-4 mb-6">
              <div>
                <label
                  className={`block text-sm font-medium mb-2 ${
                    isDarkMode ? "text-gray-400" : "text-gray-600"
                  }`}
                >
                  Task Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="projectTaskName"
                  value={formData.projectTaskName}
                  onChange={handleChange}
                  required
                  className={`w-full px-3 py-2 border rounded focus:ring-2 focus:ring-blue-500 ${
                    isDarkMode
                      ? "bg-[#0C0E12] border-gray-700 text-white placeholder-gray-500"
                      : "bg-white border-gray-300 text-gray-900 placeholder-gray-400"
                  }`}
                />
              </div>

              <div>
                <label
                  className={`block text-sm font-medium mb-2 ${
                    isDarkMode ? "text-gray-400" : "text-gray-600"
                  }`}
                >
                  Subject
                </label>
                <input
                  type="text"
                  name="subject"
                  value={formData.subject}
                  onChange={handleChange}
                  className={`w-full px-3 py-2 border rounded focus:ring-2 focus:ring-blue-500 ${
                    isDarkMode
                      ? "bg-[#0C0E12] border-gray-700 text-white placeholder-gray-500"
                      : "bg-white border-gray-300 text-gray-900 placeholder-gray-400"
                  }`}
                />
              </div>

              <div>
                <label
                  className={`block text-sm font-medium mb-2 ${
                    isDarkMode ? "text-gray-400" : "text-gray-600"
                  }`}
                >
                  Description
                </label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  rows={4}
                  className={`w-full px-3 py-2 border rounded resize-none focus:ring-2 focus:ring-blue-500 ${
                    isDarkMode
                      ? "bg-[#0C0E12] border-gray-700 text-white placeholder-gray-500"
                      : "bg-white border-gray-300 text-gray-900 placeholder-gray-400"
                  }`}
                />
              </div>
            </div>

            {/* Status & Priority */}
            <div
              className={`px-4 py-2 mb-4 rounded ${
                isDarkMode ? "bg-gray-800/50" : "bg-gray-100"
              }`}
            >
              <h3
                className={`text-sm font-semibold ${
                  isDarkMode ? "text-gray-300" : "text-gray-700"
                }`}
              >
                Status & Priority
              </h3>
            </div>

            <div className="grid grid-cols-2 gap-4 mb-6">
              <div>
                <label
                  className={`block text-sm font-medium mb-2 ${
                    isDarkMode ? "text-gray-400" : "text-gray-600"
                  }`}
                >
                  Status <span className="text-red-500">*</span>
                </label>
                <select
                  name="status"
                  value={formData.status}
                  onChange={handleChange}
                  className={`w-full px-3 py-2 border rounded focus:ring-2 focus:ring-blue-500 ${
                    isDarkMode
                      ? "bg-[#0C0E12] border-gray-700 text-white"
                      : "bg-white border-gray-300 text-gray-900"
                  }`}
                >
                  <option value="Not Started">Not Started</option>
                  <option value="In Progress">In Progress</option>
                  <option value="On Hold">On Hold</option>
                  <option value="Deferred">Deferred</option>
                  <option value="Cancelled">Cancelled</option>
                  <option value="Completed">Completed</option>
                </select>
              </div>

              <div>
                <label
                  className={`block text-sm font-medium mb-2 ${
                    isDarkMode ? "text-gray-400" : "text-gray-600"
                  }`}
                >
                  Priority
                </label>
                <select
                  name="priority"
                  value={formData.priority}
                  onChange={handleChange}
                  className={`w-full px-3 py-2 border rounded focus:ring-2 focus:ring-blue-500 ${
                    isDarkMode
                      ? "bg-[#0C0E12] border-gray-700 text-white"
                      : "bg-white border-gray-300 text-gray-900"
                  }`}
                >
                  <option value="Low">Low</option>
                  <option value="Medium">Medium</option>
                  <option value="High">High</option>
                  <option value="Urgent">Urgent</option>
                </select>
              </div>
            </div>

            {/* Assignment */}
            <div
              className={`px-4 py-2 mb-4 rounded ${
                isDarkMode ? "bg-gray-800/50" : "bg-gray-100"
              }`}
            >
              <h3
                className={`text-sm font-semibold ${
                  isDarkMode ? "text-gray-300" : "text-gray-700"
                }`}
              >
                Assignment
              </h3>
            </div>

            <div className="relative mb-6">
              <label
                className={`block text-sm font-medium mb-2 ${
                  isDarkMode ? "text-gray-400" : "text-gray-600"
                }`}
              >
                Assigned To
              </label>
              <div className="relative">
                <input
                  type="text"
                  value={userSearch}
                  onChange={(e) => {
                    setUserSearch(e.target.value);
                    setShowUserSearch(true);
                  }}
                  onFocus={() => setShowUserSearch(true)}
                  placeholder="Search for a user..."
                  className={`w-full px-3 py-2 pl-10 border rounded focus:ring-2 focus:ring-blue-500 ${
                    isDarkMode
                      ? "bg-[#0C0E12] border-gray-700 text-white placeholder-gray-500"
                      : "bg-white border-gray-300 text-gray-900 placeholder-gray-400"
                  }`}
                />
                <Search
                  className={`absolute left-3 top-1/2 -translate-y-1/2 ${
                    isDarkMode ? "text-gray-500" : "text-gray-400"
                  }`}
                  size={16}
                />
              </div>

              {showUserSearch && userSearch && (
                <div
                  className={`absolute top-full mt-1 w-full rounded-lg shadow-xl max-h-48 overflow-y-auto z-20 border ${
                    isDarkMode
                      ? "bg-[#0C0E12] border-gray-700"
                      : "bg-white border-gray-200"
                  }`}
                >
                  {loadingUsers ? (
                    <div
                      className={`p-4 text-center ${
                        isDarkMode ? "text-gray-400" : "text-gray-500"
                      }`}
                    >
                      <Loader size={20} className="animate-spin mx-auto" />
                    </div>
                  ) : users.length > 0 ? (
                    users.map((user) => (
                      <button
                        key={user.id}
                        type="button"
                        onClick={() => selectUser(user)}
                        className={`w-full px-4 py-2 text-left transition-colors border-b last:border-b-0 ${
                          isDarkMode
                            ? "hover:bg-gray-800/50 border-gray-800/50"
                            : "hover:bg-gray-50 border-gray-100"
                        }`}
                      >
                        <div
                          className={`font-medium ${
                            isDarkMode ? "text-white" : "text-gray-900"
                          }`}
                        >
                          {user.name}
                        </div>
                        <div
                          className={`text-sm ${
                            isDarkMode ? "text-gray-400" : "text-gray-500"
                          }`}
                        >
                          {user.email}
                        </div>
                      </button>
                    ))
                  ) : (
                    <div
                      className={`p-4 text-center ${
                        isDarkMode ? "text-gray-400" : "text-gray-500"
                      }`}
                    >
                      No users found
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Timeline */}
            <div
              className={`px-4 py-2 mb-4 rounded ${
                isDarkMode ? "bg-gray-800/50" : "bg-gray-100"
              }`}
            >
              <h3
                className={`text-sm font-semibold ${
                  isDarkMode ? "text-gray-300" : "text-gray-700"
                }`}
              >
                Timeline
              </h3>
            </div>

            <div className="grid grid-cols-2 gap-4 mb-6">
              <div>
                <label
                  className={`block text-sm font-medium mb-2 ${
                    isDarkMode ? "text-gray-400" : "text-gray-600"
                  }`}
                >
                  Start Date
                </label>
                <input
                  type="date"
                  name="startDate"
                  value={formData.startDate}
                  onChange={handleChange}
                  className={`w-full px-3 py-2 border rounded focus:ring-2 focus:ring-blue-500 ${
                    isDarkMode
                      ? "bg-[#0C0E12] border-gray-700 text-white"
                      : "bg-white border-gray-300 text-gray-900"
                  }`}
                />
              </div>

              <div>
                <label
                  className={`block text-sm font-medium mb-2 ${
                    isDarkMode ? "text-gray-400" : "text-gray-600"
                  }`}
                >
                  Due Date
                </label>
                <input
                  type="date"
                  name="dueDate"
                  value={formData.dueDate}
                  onChange={handleChange}
                  className={`w-full px-3 py-2 border rounded focus:ring-2 focus:ring-blue-500 ${
                    isDarkMode
                      ? "bg-[#0C0E12] border-gray-700 text-white"
                      : "bg-white border-gray-300 text-gray-900"
                  }`}
                />
              </div>
            </div>

            {/* Progress & Time */}
            <div
              className={`px-4 py-2 mb-4 rounded ${
                isDarkMode ? "bg-gray-800/50" : "bg-gray-100"
              }`}
            >
              <h3
                className={`text-sm font-semibold ${
                  isDarkMode ? "text-gray-300" : "text-gray-700"
                }`}
              >
                Progress & Time
              </h3>
            </div>

            <div className="grid grid-cols-3 gap-4 mb-6">
              <div>
                <label
                  className={`block text-sm font-medium mb-2 ${
                    isDarkMode ? "text-gray-400" : "text-gray-600"
                  }`}
                >
                  Progress (%)
                </label>
                <input
                  type="number"
                  name="percentComplete"
                  value={formData.percentComplete}
                  onChange={handleChange}
                  min="0"
                  max="100"
                  className={`w-full px-3 py-2 border rounded focus:ring-2 focus:ring-blue-500 ${
                    isDarkMode
                      ? "bg-[#0C0E12] border-gray-700 text-white"
                      : "bg-white border-gray-300 text-gray-900"
                  }`}
                />
              </div>

              <div>
                <label
                  className={`block text-sm font-medium mb-2 ${
                    isDarkMode ? "text-gray-400" : "text-gray-600"
                  }`}
                >
                  Estimated Hours
                </label>
                <input
                  type="number"
                  name="estimatedHours"
                  value={formData.estimatedHours}
                  onChange={handleChange}
                  step="0.5"
                  min="0"
                  className={`w-full px-3 py-2 border rounded focus:ring-2 focus:ring-blue-500 ${
                    isDarkMode
                      ? "bg-[#0C0E12] border-gray-700 text-white"
                      : "bg-white border-gray-300 text-gray-900"
                  }`}
                />
              </div>

              <div>
                <label
                  className={`block text-sm font-medium mb-2 ${
                    isDarkMode ? "text-gray-400" : "text-gray-600"
                  }`}
                >
                  Actual Hours
                </label>
                <input
                  type="number"
                  name="actualHours"
                  value={formData.actualHours}
                  onChange={handleChange}
                  step="0.5"
                  min="0"
                  className={`w-full px-3 py-2 border rounded focus:ring-2 focus:ring-blue-500 ${
                    isDarkMode
                      ? "bg-[#0C0E12] border-gray-700 text-white"
                      : "bg-white border-gray-300 text-gray-900"
                  }`}
                />
              </div>
            </div>

            {/* Blocking Status */}
            <div
              className={`px-4 py-2 mb-4 rounded ${
                isDarkMode ? "bg-gray-800/50" : "bg-gray-100"
              }`}
            >
              <h3
                className={`text-sm font-semibold ${
                  isDarkMode ? "text-gray-300" : "text-gray-700"
                }`}
              >
                Blocking Status
              </h3>
            </div>

            <div className="space-y-4 mb-6">
              <label
                className={`flex items-center gap-2 text-sm cursor-pointer ${
                  isDarkMode ? "text-gray-300" : "text-gray-700"
                }`}
              >
                <input
                  type="checkbox"
                  name="isBlocked"
                  checked={formData.isBlocked}
                  onChange={handleChange}
                  className={`w-4 h-4 rounded border focus:ring-2 focus:ring-blue-500 ${
                    isDarkMode
                      ? "border-gray-700 bg-[#0C0E12] text-blue-600"
                      : "border-gray-300 bg-white text-blue-600"
                  }`}
                />
                Task is blocked
              </label>

              {formData.isBlocked && (
                <div>
                  <label
                    className={`block text-sm font-medium mb-2 ${
                      isDarkMode ? "text-gray-400" : "text-gray-600"
                    }`}
                  >
                    Blocked Reason
                  </label>
                  <textarea
                    name="blockedReason"
                    value={formData.blockedReason}
                    onChange={handleChange}
                    rows={3}
                    className={`w-full px-3 py-2 border rounded resize-none focus:ring-2 focus:ring-blue-500 ${
                      isDarkMode
                        ? "bg-[#0C0E12] border-gray-700 text-white placeholder-gray-500"
                        : "bg-white border-gray-300 text-gray-900 placeholder-gray-400"
                    }`}
                    placeholder="Explain why this task is blocked..."
                  />
                </div>
              )}
            </div>
          </form>
        </div>

        {/* Footer */}
        <div
          className={`px-6 py-4 border-t flex justify-end gap-3 ${
            isDarkMode
              ? "border-gray-700 bg-[#0C0E12]"
              : "border-gray-200 bg-gray-50"
          }`}
        >
          <button
            type="button"
            onClick={onClose}
            className={`px-4 py-2 text-sm font-medium border rounded transition-colors ${
              isDarkMode
                ? "text-gray-300 bg-[#13161B] border-gray-700 hover:bg-gray-700"
                : "text-gray-700 bg-white border-gray-300 hover:bg-gray-50"
            }`}
          >
            Cancel
          </button>
          <button
            type="submit"
            form="projectTaskForm"
            disabled={loading}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {loading && <Loader size={16} className="animate-spin" />}
            {loading ? "Saving..." : "Save Changes"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default EditProjectTaskModal;
